import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './MastitisTimeline.css';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const MastitisTimeline = () => {
  const timelineRef = useRef(null);
  const progressLineRef = useRef(null);

  useEffect(() => {
    const timelineSection = timelineRef.current;
    const progressLine = progressLineRef.current;
    const steps = timelineSection.querySelectorAll('.timeline-step');

    // Set initial states - don't hide the step container itself
    gsap.set(progressLine, { scaleY: 0, transformOrigin: 'top center' });

    // Create main timeline for progress line - faster scrub
    gsap.timeline({
      scrollTrigger: {
        trigger: timelineSection,
        start: 'top 80%',
        end: 'bottom bottom',
        scrub: 0.5, // Reduced from 1 to 0.5 for faster animation
        onUpdate: (self) => {
          // Update progress line based on scroll
          gsap.to(progressLine, {
            scaleY: self.progress,
            duration: 0.1,
            ease: 'none'
          });
        }
      }
    });

    // Animate each step individually based on scroll position
    steps.forEach((step, index) => {
      const icon = step.querySelector('.step-icon-circle');
      const year = step.querySelector('.step-year');
      const title = step.querySelector('.step-title');
      const description = step.querySelector('.step-description');
      const impact = step.querySelector('.step-impact');
      const card = step.querySelector('.step-card');

      // Set initial states for step elements
      gsap.set([icon, year], { scale: 0, opacity: 0 });
      gsap.set(card, { opacity: 0, scale: 0.8 });
      gsap.set([title, description, impact], { opacity: 0, y: 20 });

      // Create scroll trigger for icon and year first
      ScrollTrigger.create({
        trigger: step,
        start: 'top 75%',
        end: 'bottom 25%',
        onEnter: () => {
          // First animate icon and year
          const tl = gsap.timeline();
          
          // Icon animation
          tl.to(icon, {
            scale: 1,
            opacity: 1,
            duration: 0.5,
            ease: 'back.out(1.7)'
          })
          // Year badge and card appear together
          .to([year, card], {
            scale: 1,
            opacity: 1,
            duration: 0.4,
            ease: 'power2.out'
          }, '-=0.2')
          // Card content animates in immediately
          .to([title, description, impact], {
            opacity: 1,
            y: 0,
            duration: 0.4,
            stagger: 0.08,
            ease: 'power2.out'
          }, '-=0.2');
        },
        onEnterBack: () => {
          // Ensure visibility when scrolling back up
          gsap.to([icon, year], { scale: 1, opacity: 1, duration: 0.3 });
          gsap.to(card, { opacity: 1, scale: 1, duration: 0.3 });
          gsap.to([title, description, impact], { opacity: 1, y: 0, duration: 0.3 });
        }
      });
    });

    // Cleanup function
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <section ref={timelineRef} id="timeline-section" className="timeline-section">
      <div className="timeline-container">
        
        <div className="timeline-flow" id="timelineFlow">
          {/* Progress line that grows with scroll */}
          <div ref={progressLineRef} className="timeline-progress-line"></div>
          
          <div className="timeline-steps-container">
            
            {/* Step 1: 1887-1957 - Left Side */}
            <div className="timeline-step" data-step="1">
              <div className="step-content">
                <div className="step-card">
                  <div className="step-title">Discovery & Early Diagnostics</div>
                  <div className="step-description">
                    <strong>1887:</strong> Nocard and Mollereau isolate Strep. agalactiae in cattle; French researchers identify Contagious Agalactia in sheep/goats.<br/><br/>
                    <strong>1945:</strong> Penicillin deployed—effective for cows but requires different dosages for small ruminants due to faster metabolic rates.
                  </div>
                  <div className="step-impact">Foundation Era</div>
                </div>
              </div>
              
              <div className="step-icon-wrapper">
                <div className="step-icon-circle">
                  <div className="step-icon">🔬</div>
                </div>
                <div className="step-year">1887 - 1957</div>
              </div>
              
              <div></div>
            </div>

            {/* Step 2: 1975-1980s - Right Side */}
            <div className="timeline-step" data-step="2">
              <div></div>
              
              <div className="step-icon-wrapper">
                <div className="step-icon-circle">
                  <div className="step-icon">🌍</div>
                </div>
                <div className="step-year">1975 - 1980s</div>
              </div>
              
              <div className="step-content">
                <div className="step-card">
                  <div className="step-title">Global Standards & Species Diversity</div>
                  <div className="step-description">
                    <strong>1975:</strong> First standardized SCC benchmarks for dairy sheep in Mediterranean (Roquefort/Manchego industries).<br/><br/>
                    <strong>1980s:</strong> Camel milk research discovers unique protective proteins (Lysozymes, Lactoferrin) providing natural mastitis resistance.
                  </div>
                  <div className="step-impact">Species Recognition</div>
                </div>
              </div>
            </div>

            {/* Step 3: 2000-2015 - Left Side */}
            <div className="timeline-step" data-step="3">
              <div className="step-content">
                <div className="step-card">
                  <div className="step-title">Molecular Revolution</div>
                  <div className="step-description">
                    <strong>2000:</strong> PCR becomes "Gold Standard" for detecting Staph. aureus across all livestock species in mixed herds.<br/><br/>
                    <strong>2015:</strong> MALDI-TOF protein analysis maps Dromedary Camel pathogens, leading to first commercial camel mastitis vaccines.
                  </div>
                  <div className="step-impact">Precision Medicine</div>
                </div>
              </div>
              
              <div className="step-icon-wrapper">
                <div className="step-icon-circle">
                  <div className="step-icon">🧬</div>
                </div>
                <div className="step-year">2000 - 2015</div>
              </div>
              
              <div></div>
            </div>

            {/* Step 4: 2020-2023 - Right Side */}
            <div className="timeline-step" data-step="4">
              <div></div>
              
              <div className="step-icon-wrapper">
                <div className="step-icon-circle">
                  <div className="step-icon">📡</div>
                </div>
                <div className="step-year">2020 - 2023</div>
              </div>
              
              <div className="step-content">
                <div className="step-card">
                  <div className="step-title">Smart Tech & Sustainability</div>
                  <div className="step-description">
                    <strong>2020:</strong> Wearable tech (accelerometers, smart collars) miniaturized for goats/sheep to monitor grazing behavior as early udder pain indicator.<br/><br/>
                    <strong>2023:</strong> Selective Dry Cow Therapy adapted for all species to prevent antibiotic residues in specialty cheeses.
                  </div>
                  <div className="step-impact">IoT & Stewardship</div>
                </div>
              </div>
            </div>

            {/* Step 5: 2025-2026 - Left Side */}
            <div className="timeline-step" data-step="5">
              <div className="step-content">
                <div className="step-card">
                  <div className="step-title">Multi-Species AI Future</div>
                  <div className="step-description">
                    <strong>2025-2026:</strong> Machine learning uses Species-Specific Algorithms instead of one-size-fits-all approaches.<br/><br/>
                    AI compares each animal's milk conductivity against species-specific databases, predicting mastitis 48 hours early while accounting for unique biology of cows, goats, sheep, and camels.
                  </div>
                  <div className="step-impact">AI-Driven Precision</div>
                </div>
              </div>
              
              <div className="step-icon-wrapper">
                <div className="step-icon-circle">
                  <div className="step-icon">🤖</div>
                </div>
                <div className="step-year">2025 - 2026</div>
              </div>
              
              <div></div>
            </div>

          </div>
        </div>

      </div>
    </section>
  );
};

export default MastitisTimeline;